import { useEffect, useState, useMemo, useCallback } from 'react';

// 기기 핑거프린트 - 동일 기기(브라우저)에서 반복 가입해 웰컴/추천 보상을 계속
// 받아가는 어뷰징을 막기 위한 1차 방어. 시크릿모드/캐시삭제/다른 브라우저로는
// 우회 가능하지만(완벽 차단 아님), 캐주얼한 반복가입은 확실히 막는다.
function getDeviceFingerprint(): string {
  try {
    const nav = window.navigator;
    const screenStr = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let canvasStr = '';
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('handy-fp-🔒', 2, 2);
        canvasStr = canvas.toDataURL();
      }
    } catch {
      // 캔버스 차단된 브라우저 - 나머지 값만으로 계속 진행
    }
    const raw = [nav.userAgent, nav.language, screenStr, tz, nav.hardwareConcurrency, canvasStr].join('|');
    let hash = 5381;
    for (let i = 0; i < raw.length; i++) {
      hash = (hash * 33) ^ raw.charCodeAt(i);
    }
    return `fp_${(hash >>> 0).toString(36)}_${raw.length}`;
  } catch {
    return '';
  }
}
import { apiUtils } from '../sdk/api';

// GPT 문서에 따른 통일된 키 사용
const ATK = 'handy-jwt-token';
const RTK = 'handy-refresh-token';
const WADDR = 'handy-wallet-address';
const LTIME = 'handy-last-login-time';

function computeDefaultApiBase() {
  if (typeof window === 'undefined') return 'https://wallet.handy.app';
  try {
    const url = new URL(window.location.origin);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = 'localhost';
      url.port = '3600';
      url.protocol = 'http:';
      return url.origin;
    }
    return url.origin;
  } catch (error) {
    return 'https://wallet.handy.app';
  }
}

export interface User {
  id?: number;
  nickname: string;
  walletAddress: string;
  referralCode?: string;
  referredBy?: string;
  balance?: number;
  tier?: string;
  createdAt?: string;
}

export function useSession() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rawApiBase = (import.meta as any)?.env?.VITE_API_URL ?? '';
  const resolvedApiBase = useMemo(() => {
    const candidate = rawApiBase?.trim();
    const sanitized = candidate ? (candidate.endsWith('/') ? candidate.slice(0, -1) : candidate) : '';
    if (sanitized) return sanitized;
    const fallback = computeDefaultApiBase();
    return fallback.endsWith('/') ? fallback.slice(0, -1) : fallback;
  }, [rawApiBase]);

  const buildUrl = useCallback(
    (path: string) => (resolvedApiBase ? `${resolvedApiBase}${path}` : path),
    [resolvedApiBase],
  );

  const fetchBalance = useCallback(
    async (walletAddress?: string) => {
      if (!walletAddress) return undefined;
      try {
        const balanceRes = await fetch(buildUrl(`/api/blockchain/vttn/balance/${walletAddress}`));
        if (balanceRes.ok) {
          const balanceData = await balanceRes.json();
          return parseFloat(balanceData.balance);
        }
      } catch (balErr) {
        console.warn('wHANDY 잔액 조회 실패, DB 값 사용:', balErr);
      }
      return undefined;
    },
    [buildUrl],
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await apiUtils.getMe();
        if (r.ok) {
          const userData = await r.json();
          const resolvedBalance = await fetchBalance(userData.walletAddress);
          if (typeof resolvedBalance === 'number') userData.balance = resolvedBalance;
          if (userData.walletAddress) localStorage.setItem(WADDR, userData.walletAddress);
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Session restore failed:', err);
        setError('세션 복구 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchBalance]);

  // 참여/구매 등으로 잔액이 바뀐 직후 화면 전체에 반영 (2026-10-02)
  useEffect(() => {
    const refresh = async () => {
      try {
        const r = await apiUtils.getMe();
        if (!r.ok) return;
        const userData = await r.json();
        const resolvedBalance = await fetchBalance(userData.walletAddress);
        if (typeof resolvedBalance === 'number') userData.balance = resolvedBalance;
        setUser(userData);
      } catch { /* 갱신 실패는 조용히 */ }
    };
    window.addEventListener('handy:balance-refresh', refresh);
    return () => window.removeEventListener('handy:balance-refresh', refresh);
  }, [fetchBalance]);

  const login = async (nickname: string, pin: string) => {
    try {
      setError(null);
      setLoading(true);
      const r = await apiUtils.login(nickname, pin);
      if (!r.ok) {
        const errorData = await r.json();
        throw new Error(errorData.error || '로그인 실패');
      }
      const data = await r.json();
      const resolvedBalance = await fetchBalance(data.user.walletAddress);
      if (typeof resolvedBalance === 'number') data.user.balance = resolvedBalance;
      localStorage.setItem(ATK, data.accessToken);
      localStorage.setItem(RTK, data.refreshToken);
      localStorage.setItem(WADDR, data.user.walletAddress);
      localStorage.setItem(LTIME, String(Date.now()));
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (nickname: string, pin: string, phoneNumber?: string, fullName?: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await fetch(buildUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname, pin, phoneNumber, fullName, deviceFingerprint: getDeviceFingerprint() }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '회원가입 실패');
      }
      const data = await response.json();
      const resolvedBalance = await fetchBalance(data.user?.walletAddress);
      if (typeof resolvedBalance === 'number') data.user.balance = resolvedBalance;
      if (data.token) {
        localStorage.setItem(ATK, data.token);
        localStorage.setItem(RTK, data.refreshToken);
        localStorage.setItem(WADDR, data.user.walletAddress);
        localStorage.setItem(LTIME, String(Date.now()));
        setUser(data.user);
      }
      return { success: true, user: data.user || data };
    } catch (err: any) {
      console.error('회원가입 오류:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const validateReferralCode = async (code: string) => {
    try {
      const response = await fetch(buildUrl(`/api/referrals/validate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('handy-jwt-token')}`,
        },
        body: JSON.stringify({ referralCode: code }),
      });
      if (response.ok) {
        const data = await response.json();
        return { success: true, ...data };
      }
      const errorData = await response.json();
      return { success: false, error: errorData.error || '추천코드 검증에 실패했습니다.' };
    } catch (error) {
      console.error('추천코드 검증 실패:', error);
      return { success: false, error: '추천코드 검증 중 오류가 발생했습니다.' };
    }
  };

  const logout = async () => {
    try {
      const r = await fetch(buildUrl('/api/auth/logout'), { method: 'POST' });
      if (!r.ok) {
        const errorData = await r.json();
        console.warn('Logout warning:', errorData);
      }
    } catch (err) {
      console.error('Logout API failed:', err);
    } finally {
      for (const k of [ATK, RTK, WADDR, LTIME]) localStorage.removeItem(k);
      setUser(null);
      setError(null);
    }
  };

  const clearError = () => setError(null);

  const claimWelcomeBonus = async () => {
    try {
      setError(null);
      setLoading(true);
      const r = await apiUtils.claimWelcomeBonus();
      if (!r.ok) {
        const errorData = await r.json();
        throw new Error(errorData.error || '웰컴 보너스 클레임에 실패했습니다.');
      }
      const data = await r.json();
      return { success: true, data };
    } catch (err: any) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    clearError,
    validateReferralCode,
    claimWelcomeBonus,
    isAuthenticated: !!user,
    isLoading: loading,
    accessToken: localStorage.getItem(ATK),
  };
}